// UserContext — single source of truth for the authenticated user.
//
// Replaces prop-drilling of userId / username / email / permissions /
// isLoggedIn (previously threaded App -> HomePage -> ~23 components).
// Any component can read the current user with useUser().

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { API_URL } from "../services/api";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/users/${id}/permissions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      setPermissions((data.permissions || []).filter((p) => p !== null));
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  }, []);

  // Hydrate from localStorage on mount.
  const hydrate = useCallback(async () => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      setIsLoggedIn(true);
      setUserId(storedUserId);
      setUsername(localStorage.getItem("username") || "");
      setEmail(localStorage.getItem("email") || "");
      await fetchPermissions(storedUserId);
    }
    setIsLoading(false);
  }, [fetchPermissions]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback((id, name, mail, token, perms) => {
    localStorage.setItem("userId", id);
    localStorage.setItem("username", name);
    localStorage.setItem("email", mail);
    if (token) localStorage.setItem("token", token);
    setUserId(id);
    setUsername(name);
    setEmail(mail);
    setPermissions(perms || []);
    setIsLoggedIn(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    setUserId(null);
    setUsername("");
    setEmail("");
    setPermissions([]);
    setIsLoggedIn(false);
  }, []);

  // Re-read profile + permissions (replaces the old setTriggerReload toggle).
  const refresh = useCallback(() => {
    setUsername(localStorage.getItem("username") || "");
    setEmail(localStorage.getItem("email") || "");
    const id = localStorage.getItem("userId");
    if (id) {
      setUserId(id);
      fetchPermissions(id);
    }
  }, [fetchPermissions]);

  const value = {
    userId,
    username,
    email,
    permissions,
    isLoggedIn,
    isLoading,
    login,
    logout,
    refresh,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (ctx === null) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
};

export default UserContext;
